import AltInstance from '../../../../Common/Globals/Scripts/AltInstance';
import { fromJS as ImmutableFromJs } from 'immutable';
import RatesSearchActions from '../Actions/RatesSearchActions';
import { getDefaultSearchCriteria } from '../Business/RatesSearch';
import ProfileStore from '../../../../Common/Stores/ProfileStore.ts';

function getLookupsForOwner(lookups, ownerId) {
    return {
        rateCardTypes: lookups.rateCardTypes.filter(rateCardType => rateCardType.owner.businessId == ownerId),
        customers: lookups.customers.filter(customer => customer.owner.businessId == ownerId),
        suppliers: lookups.suppliers.filter(supplier => supplier.owner.businessId == ownerId),
        soldToParties: lookups.soldToParties.filter(soldToParty => soldToParty.owner.businessId == ownerId),
        owners: lookups.owners, // The complete owner lookup is always returned. It is a meta-filter.
        rateActivities: lookups.rateActivities.filter(rateActivity => rateActivity.owner.businessId == ownerId),
        rateChargeTypes: lookups.rateChargeTypes.filter(rateChargeType => rateChargeType.owner.businessId == ownerId),
        rateProducts: lookups.rateProducts.filter(rateProduct => rateProduct.owner.businessId == ownerId),
        fromZones: lookups.fromZones.filter(fromZone => fromZone.owner.businessId == ownerId),
        toZones: lookups.toZones.filter(toZone => toZone.owner.businessId == ownerId)
    };
}

function extractOwnersAsOptions(owners) {
    return owners.map(owner => { return { id: owner.businessId, name: owner.name } });
}

function extractRateCardTypesAsOptions(rateCardTypes) {
    return rateCardTypes.map(rateCardType => { return { id: rateCardType.rateCardType.rateCardTypeId, name: rateCardType.rateCardType.name } });
}

function extractBusinessesAsOptions(businesses) {
    return businesses.map(business => { return { id: business.business.businessId, name: business.business.name } });
}

function extractZonesAsOptions(zones) {
    return zones.map(zone => { return { id: zone.zone.rateZoneId, name: zone.zone.name } });
}

function extractRateActivities(rateActivities) {
    return rateActivities.map(rateActivity => { return { id: rateActivity.rateActivity.rateActivityId, name: rateActivity.rateActivity.name } });
}

function extractRateChargeTypes(rateChargeTypes) {
    return rateChargeTypes.map(rateChargeType => { return { id: rateChargeType.rateChargeType.rateChargeTypeId, name: rateChargeType.rateChargeType.name } });
}

function extractRateProducts(rateProducts) {
    return rateProducts.map(rateProduct => { return { id: rateProduct.rateProduct.rateProductId, name: rateProduct.rateProduct.name } });
}

function sortByNameAndId(a, b) {
    var nameA = a.name.toUpperCase(); // ignore upper and lowercase
    var nameB = b.name.toUpperCase(); // ignore upper and lowercase
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    // names must be equal, compare id
    return (a.id - b.id);
}

function getLoggedInBusinessIdOrNull() {
    const profile = ProfileStore.getState().profile;
    const businessProfile = profile.get('business');
    if (businessProfile) {
        const businessId = businessProfile.get('businessResourceId');
        return businessId;
    }
    return null;
}

function getDefaultSelectedOwnerFromOwners(allOwners) {
    const loggedInBusinessId = getLoggedInBusinessIdOrNull();
    if (loggedInBusinessId) {
        const loggedInBusinessAsOwner = allOwners.find(owner => owner.id == loggedInBusinessId);
        if (loggedInBusinessAsOwner)
            return loggedInBusinessAsOwner;
    }
    const defaultSelectedOwner = allOwners[0];
    return defaultSelectedOwner;
}

function getUserCanDeleteCurrentRates(ownerIdForCurrentRates) {
    const loggedInBusinessIdOrNull = getLoggedInBusinessIdOrNull();
    return (ownerIdForCurrentRates == loggedInBusinessIdOrNull) || (loggedInBusinessIdOrNull == null);
}

export class RatesSearchStore {
    constructor() {
        this.bindActions(RatesSearchActions);
        this.options = this.constructOptions([], [], [], [], [], [], [], [], [], []);
        this.selectedOwner = null;
        this.rateCardsToView = ImmutableFromJs(null);
        this.onResetSearchCriteria();
        this.resultVersion = 0;
        this.searchCriteriaLoaded = false;
        this.authorisations = {};
        this.userCanDeleteCurrentRates = false;
    }

    constructOptions(rateCardTypes, customers, suppliers, soldToParties, owners, rateActivities, rateChargeTypes, rateProducts, fromZones, toZones) {
        return ImmutableFromJs(
                Object.assign(
                    getDefaultSearchCriteria(),
                    {
                        rateCardTypes,
                        customers,
                        suppliers,
                        soldToParties,
                        owners,
                        rateActivities,
                        rateChargeTypes,
                        rateProducts,
                        fromZones,
                        toZones
                    })
            );
    }

    onSelectionChangedInUi([rateCard, isSelected]) {
        const rateCardWithUiSet = rateCard.set('isSelectedInUi', isSelected);
        const rateCardIndex = this.rateCardsToView.indexOf(rateCard);
        this.rateCardsToView = this.rateCardsToView.set(rateCardIndex, rateCardWithUiSet);
    }

    onRateCardsDeleted(rateCardIds) {
        this.rateCardsToView = this.rateCardsToView.filter(rc => !rateCardIds.includes(rc.get('rateCardId')))
    }

    onSearchCriteriaLookupsLoaded(lookups) {
        this.searchCriteriaLoaded = true;
        this.lookUps = lookups;
        this.owners = extractOwnersAsOptions(lookups.owners).sort(sortByNameAndId);
        const defaultSelectedOwner = getDefaultSelectedOwnerFromOwners(this.owners);
        this.onOwnerSelectionChanged(defaultSelectedOwner.id);
    }

    onOwnerSelectionChanged(ownerId) {
        this.selectedOwner = this.owners.find(owner => owner.id == ownerId);
        var lookupsForOwner = getLookupsForOwner(this.lookUps, this.selectedOwner.id);
        this.options = this.constructOptions(
            extractRateCardTypesAsOptions(lookupsForOwner.rateCardTypes),
            extractBusinessesAsOptions(lookupsForOwner.customers),
            extractBusinessesAsOptions(lookupsForOwner.suppliers),
            extractBusinessesAsOptions(lookupsForOwner.soldToParties),
            extractOwnersAsOptions(this.lookUps.owners),
            extractRateActivities(lookupsForOwner.rateActivities),
            extractRateChargeTypes(lookupsForOwner.rateChargeTypes),
            extractRateProducts(lookupsForOwner.rateProducts),
            extractZonesAsOptions(lookupsForOwner.fromZones),
            extractZonesAsOptions(lookupsForOwner.toZones)
        );

        this.onResetSearchCriteria();
    }

    onSearchCriteriaChanged(searchCriteria) {
        this.searchCriteria = searchCriteria;
    }

    onResetSearchCriteria() {
        var searchCriteria = this.options;
        if (this.selectedOwner != null) {
            searchCriteria = this.options.set('owners', ImmutableFromJs([this.selectedOwner]));
        }
        this.searchCriteria = searchCriteria;
    }

    onViewableRatesLoaded(rateCardsToView){
        this.rateCardsToView = ImmutableFromJs(rateCardsToView);
        this.userCanDeleteCurrentRates = this.selectedOwner && getUserCanDeleteCurrentRates(this.selectedOwner.id);
        ++this.resultVersion;
    }

    onCommandAuthorisationsLoaded(authorisations) {
        this.authorisations = authorisations;
    }
}

export default AltInstance.createStore(RatesSearchStore, 'RatesSearchStore');
